import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import api, { mediaUrl } from "../../lib/api";
import ImageUploader from "./ImageUploader";

export function ProjectsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/projects/admin");
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/api/projects/admin/${p.id}`);
    load();
  };

  const togglePublish = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    await api.patch(`/api/projects/admin/${p.id}`, { status: next });
    load();
  };

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">Studio</span>
          <h1 style={{ marginTop: 8 }}>Projects</h1>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["all", "draft", "published"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className="chip"
              style={{
                cursor: "pointer",
                background: filter === f ? "var(--accent)" : "transparent",
                color: filter === f ? "#0a0a0a" : "var(--text-soft)",
                borderColor: filter === f ? "var(--accent)" : "var(--border-strong)",
                textTransform: "capitalize",
              }}
            >
              {f}
            </button>
          ))}
          <button type="button" className="btn" onClick={() => nav("/admin/projects/new")}>
            New project <span className="arrow"><HiArrowUpRight /></span>
          </button>
        </div>
      </div>

      {loading && <p className="text-muted">Loading…</p>}
      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      {!loading && !error && (() => {
        const filtered = items.filter((p) => filter === "all" || (p.status || "draft") === filter);
        return (
        <div className="admin-card" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 36, textAlign: "center", color: "var(--muted)" }}>
              {items.length === 0 ? (
                <>No projects yet. <Link to="/admin/projects/new" style={{ color: "var(--accent)" }}>Create your first one</Link>.</>
              ) : `No projects with status "${filter}".`}
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Category</th>
                  <th>Featured</th>
                  <th>Order</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/admin/projects/${p.id}`}>{p.title}</Link>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                        {p.role}
                      </div>
                    </td>
                    <td><span className={`status-pill ${p.status === "published" ? "published" : "draft"}`}>{p.status || "draft"}</span></td>
                    <td style={{ fontSize: 13 }}>{p.category || "—"}</td>
                    <td>{p.featured ? <span className="status-pill published">featured</span> : <span className="status-pill draft">no</span>}</td>
                    <td style={{ color: "var(--muted)", fontSize: 13 }}>{p.order ?? 0}</td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 8 }}>
                        <button type="button" className="chip" style={{ cursor: "pointer" }} onClick={() => togglePublish(p)}>
                          {p.status === "published" ? "Unpublish" : "Publish"}
                        </button>
                        <Link className="chip" to={`/admin/projects/${p.id}`}>Edit</Link>
                        <a className="chip" href={p.url} target="_blank" rel="noreferrer noopener">Visit</a>
                        <button type="button" className="chip" style={{ cursor: "pointer", color: "var(--danger)", borderColor: "rgba(255,107,107,0.3)" }} onClick={() => remove(p)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );})()}
    </div>
  );
}

const EMPTY = {
  title: "", url: "", image: "", category: "", role: "",
  featured: false, order: 0, slug: "", status: "draft",
};

export function ProjectsEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();

  const [item, setItem] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let live = true;
    api.get(`/api/projects/admin/${id}`)
      .then(({ data }) => { if (live) setItem({ ...EMPTY, ...data.item }); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, isNew]);

  const set = (k, v) => setItem((p) => ({ ...p, [k]: v }));

  const persist = async (overrides = {}) => {
    if (!item.title) { setError("Title required."); return; }
    setBusy(true); setError(null);
    try {
      const body = { ...item, ...overrides };
      if (isNew) {
        const { data } = await api.post("/api/projects/admin", body);
        nav(`/admin/projects/${data.item.id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/api/projects/admin/${id}`, body);
        setItem({ ...EMPTY, ...data.item });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = () => persist();
  const publish = () => persist({ status: "published" });
  const unpublish = () => persist({ status: "draft" });

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">{isNew ? "New" : "Edit"} project</span>
          <h1 style={{ marginTop: 8 }}>{item.title || "Untitled"}</h1>
          {!isNew && (
            <span className={`status-pill ${item.status === "published" ? "published" : "draft"}`} style={{ marginTop: 8 }}>
              {item.status || "draft"}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link className="btn btn--ghost" to="/admin/projects">Back</Link>
          <button type="button" className="btn btn--ghost" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save draft"}
          </button>
          {item.status === "published" ? (
            <button type="button" className="btn btn--ghost" onClick={unpublish} disabled={busy}>
              Unpublish
            </button>
          ) : (
            <button type="button" className="btn" onClick={publish} disabled={busy}>
              {isNew ? "Save & publish" : "Publish"}
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      <div className="admin-card" style={{ maxWidth: 880 }}>
        <div className="row">
          <div className="field">
            <label>Title</label>
            <input className="input" value={item.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div className="field">
            <label>Slug</label>
            <input className="input" value={item.slug || ""} placeholder="auto-from-title" onChange={(e) => set("slug", e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label>URL</label>
          <input className="input" value={item.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
        </div>

        <div className="row">
          <div className="field">
            <label>Category</label>
            <input className="input" value={item.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Shopify Plus · D2C" />
          </div>
          <div className="field">
            <label>Role / Note</label>
            <input className="input" value={item.role} onChange={(e) => set("role", e.target.value)} placeholder="What you did" />
          </div>
        </div>

        <ImageUploader
          label="Cover image"
          value={item.image}
          onChange={(u) => set("image", u)}
          aiEnabled
          aiContext={{ title: item.title, excerpt: item.role, tags: item.category ? [item.category] : [] }}
        />

        <div className="row">
          <div className="field">
            <label>Order (lower = first)</label>
            <input className="input" type="number" value={item.order} onChange={(e) => set("order", e.target.value)} />
          </div>
          <div className="field">
            <label>Featured (homepage)</label>
            <select className="select" value={item.featured ? "1" : "0"} onChange={(e) => set("featured", e.target.value === "1")}>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </div>
        </div>

        {item.image && (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
            Preview path: <a href={mediaUrl(item.image)} target="_blank" rel="noreferrer noopener" style={{ color: "var(--accent)" }}>{item.image}</a>
          </div>
        )}
      </div>
    </div>
  );
}
